import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { DeleteButton, Dropzone, ToggleableButton } from '~/components'
import { acceptedImageTypes } from '~/pages/paragraphs/data'
import { useParagraphContext } from './hooks'

const Preview = () => {
  const { t } = useTranslation()

  const { editable, paragraph, deleteImage, uploadImage } = useParagraphContext(
    Preview.name
  )

  const [imageError, setImageError] = useState()
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    setImageError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraph.media])

  const onImageError = useCallback(() => {
    setImageError(t('paragraph.image-error'))
  }, [t, setImageError])

  return (
    <Dropzone
      accept={acceptedImageTypes}
      maxFileSize={600 * 1024}
      multiple={false}
      sx={{
        minHeight: '80px',
        ...(!imageError
          ? {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 0,
              p: 0,
              '& img': {
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              },
            }
          : { px: 1.5, py: 1 }),
        overflow: 'clip',
        '& img': {
          objectFit: showPreview ? 'contain' : 'cover',
          width: '100%',
          height: showPreview ? '100%' : '80px',
        },
      }}
      onDrop={async (files) => {
        setImageError()
        await uploadImage(true, files[0])
        setShowPreview(true)
      }}
    >
      {imageError ? (
        <Typography sx={{ color: 'danger' }} variant="body">
          {imageError}
        </Typography>
      ) : (
        <>
          {paragraph.preview ? (
            <LazyLoadImage src={paragraph.preview} onError={onImageError} />
          ) : (
            t('paragraph.movie.preview')
          )}
          {paragraph.preview && (
            <Stack
              spacing={0.5}
              sx={{
                p: 1,
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'divider',
                },
              }}
            >
              <ToggleableButton
                background
                disabled={!editable}
                icon={{ on: <VisibilityOffIcon />, off: <VisibilityIcon /> }}
                size="small"
                on={showPreview}
                onChange={setShowPreview}
              />
              <DeleteButton
                disabled={!editable}
                onClick={async (e) => {
                  e.stopPropagation()
                  await deleteImage()
                  setShowPreview(false)
                }}
              />
            </Stack>
          )}
        </>
      )}
    </Dropzone>
  )
}

export default Preview
